import React from "react";
// import PropTypes from 'prop-types';
import classNames from "classnames";
import { graphql, useStaticQuery, Link } from "gatsby"; // Link

// Components
import WrapRotation from "./WrapRotation";
import WrapMouseMove from "./WrapMouseMove";
import WrapLink from "./WrapLink";
import WrapArrow from "./WrapArrow";

// Primitives
import EmojiIcon from "./EmojiIcon";

/* The menu/overlay always exists in the DOM, but it is visually hidden
 * by default. That way it is always accessible to screen readers.
 * This components state will toggle if its visually hidden or not.
 */
const HamburgerMenu = ({ toggleMenu, isOpen }) => {
  const { sanitySiteSettings } = useStaticQuery(
    graphql`
      query {
        sanitySiteSettings {
          secondaryMenu {
            text
            internalPage {
              ... on SanityArticle {
                id
                _type
                slug {
                  current
                }
                title
              }
              ... on SanityPages {
                id
                slug {
                  current
                }
                title
              }
            }
            url
          }
          globalMenu {
            text
            internalPage {
              ... on SanityArticle {
                id
                _type
                slug {
                  current
                }
                title
              }
              ... on SanityPages {
                id
                slug {
                  current
                }
                title
              }
            }
            url
          }
        }
      }
    `
  );

  const { globalMenu = [], secondaryMenu = [] } = sanitySiteSettings;

  // Primary menu links
  const RenderGlobalLinks = () => {
    return globalMenu.map((link, i) => {
      const { text, url, internalPage } = link;

      if (internalPage) {
        const { slug } = internalPage;
        return (
          <li
            className="
              group
              flex justify-center
              mb-4 font-sans-symbols
              [font-feature-settings:'ss00']
            "
            key={`menu_item_${i}`}
          >
            <WrapRotation className="" introDirection="fromLeft">
              <WrapMouseMove>
                <WrapLink
                  className="text-2xl pointer-events-auto hover:motion-safe:animate-symbol-bounce"
                  color="green"
                  href={`/${slug.current}`}
                >
                  {/* Optional icons before */}
                  {text.toLowerCase() === "tickets" && (
                    <>
                      <WrapArrow direction="right" />
                      <WrapArrow direction="right" />
                    </>
                  )}
                  {text.toLowerCase() === "program" && (
                    <WrapArrow direction="right" />
                  )}
                  {text.toLowerCase() === "speakers" && (
                    <EmojiIcon
                      className="mr-2"
                      name="leadership"
                      animatedHover
                    />
                  )}
                  {/* Link text */}
                  {text}
                  {/* Optional icons after */}
                  {text.toLowerCase() === "tickets" && (
                    <>
                      <WrapArrow direction="left" />
                      <WrapArrow direction="left" />
                    </>
                  )}
                  {text.toLowerCase() === "program" && (
                    <WrapArrow direction="left" />
                  )}
                  {text.toLowerCase() === "speakers" && (
                    <EmojiIcon className="ml-2" name="stage" animatedHover />
                  )}
                  {text.toLowerCase() === "workshops" && (
                    <EmojiIcon className="ml-2" name="workshop" animatedHover />
                  )}
                </WrapLink>
              </WrapMouseMove>
            </WrapRotation>
          </li>
        );
      }
      return (
        <li
          className="
            mb-4 pointer-events-none
          "
          key={`menu_item_${i}`}
        >
          <WrapRotation className="" introDelay={0.2} introDirection="fromLeft">
            <WrapMouseMove>
              <a
                className="
                  text-2xl uppercase
                  bg-green text-black
                  pointer-events-normal
                "
                href={url}
                rel={`noopener noreferrer`}
              >
                {text}
              </a>
            </WrapMouseMove>
          </WrapRotation>
        </li>
      );
    });
  };

  // Secondary menu links
  const RenderSecondaryLinks = () => {
    return secondaryMenu.map((link, i) => {
      const { text, url, internalPage } = link;

      if (internalPage) {
        const { slug } = internalPage;
        return (
          <li
            className="
              flex
              pointer-events-none
            "
            key={`menu_item_${i}`}
          >
            <Link
              className="group text-md cursor-pointer pointer-events-auto text-white hover:text-green"
              to={`/${slug.current}`}
            >
              <WrapArrow direction="right" />
              <span className="ml-1">{text}</span>
            </Link>
          </li>
        );
      }
      return (
        <li
          className="
            flex
            pointer-events-none
          "
          key={`menu_item_${i}`}
        >
          <a
            className="
              group text-md cursor-pointer pointer-events-auto text-white hover:text-green
              [font-feature-settings:'ss00'!important]
            "
            href={url}
            rel={`noopener noreferrer`}
          >
            <WrapArrow direction="right" />
            <span className="ml-1">{text}</span>
          </a>
        </li>
      );
    });
  };

  return (
    <>
      {/* Primary linklist */}
      <div
        className={classNames(
          isOpen
            ? "absolute lg:fixed " +
                "top-0 left-0 right-0 md:right-0 md:left-0 bottom-0 " +
                "flex flex-wrap items-start justify-end " +
                "overflow-y-auto " +
                "pt-20 md:pt-16 pb-20 transform-gpu"
            : "sr-only"
          // md:left-1/3 eventually when we get the second menu in
        )}
      >
        {/* Primary links */}
        <nav
          className="
            w-full lg:w-1/2 
            mt-16 lg:mr-16 
            pb-64 lg:pb-0
            pointer-events-auto
          "
        >
          <ul>
            <RenderGlobalLinks />
          </ul>
        </nav>
      </div>

      {/* Secondary links */}
      {isOpen ? (
        <nav
          className={classNames(
            "absolute lg:fixed bottom-0 left-[5vw] sm:left-[7.5vw] ",
            "w-full lg:w-1/3",
            "pb-2 lg:pb-8 mt-16 pointer-events-auto bg-black",
            "before:absolute before:bottom-full before:left-0 before:right-0 before:h-24 before:bg-gradient-to-t from-black",
            "pointer-events-none"
          )}
        >
          <ul>
            <RenderSecondaryLinks />
          </ul>
        </nav>
      ) : (
        ""
      )}

      {/* Menu toggle button */}
      <button
        onClick={toggleMenu}
        className={classNames("button-menu", isOpen ? "is-active" : "")}
        aria-haspopup="true"
        aria-expanded={isOpen}
        aria-label="Hovedmenyknapp"
      >
        <span className="button-menu__middle-line" />
        {/* {isOpen ? "X" : ""} */}
      </button>
    </>
  );
};

// HamburgerMenu.propTypes = {
//   toggleMenu: PropTypes.func,
//   isOpen: PropTypes.bool
// };

export default HamburgerMenu;
